import { useCallback, useMemo, useReducer } from "react";
import InternalAutopilotHydrationContext, {
  IInternalAutopilotHydrationValues,
  IAutopilotPreviewContactFeedback,
  IPreviousResultsWithFeedback,
  IScoringCriteriaResult,
  PreviewContactFeedbackMap,
} from "../../contexts/InternalAutopilotHydrationContext";
import {
  AutopilotSessionPreviewResponse,
  InternalCuratedSearchFilters,
} from "../../__generated__/graphql";

type State = {
  prompt: string;
  savedFilters: InternalCuratedSearchFilters | null;
  expandFilters: InternalCuratedSearchFilters | null;
  expandPrompt: string;
  scoringCriteriaValues: string[];
  expandScoringCriteriaValues: string[];
  previewStep: AutopilotSessionPreviewResponse | null;
  remixPreviewStep: AutopilotSessionPreviewResponse | null;
  curatedSearchId: number | null;
  remixedFilters: InternalCuratedSearchFilters | null;
  remixCuratedSearchId: number | null;
  remixPrompt: string | undefined;
  specificCompanies: string[];
  previewContactFeedback: PreviewContactFeedbackMap;
  remixPreviewContactFeedback: PreviewContactFeedbackMap;
};

type Action =
  | { type: "SET_PROMPT"; payload: string }
  | { type: "SET_SAVED_FILTERS"; payload: InternalCuratedSearchFilters | null }
  | { type: "SET_EXPAND_FILTERS"; payload: InternalCuratedSearchFilters | null }
  | { type: "SET_EXPAND_PROMPT"; payload: string }
  | { type: "SET_EXPAND_SCORING_CRITERIA_VALUES"; payload: string[] }
  | { type: "SET_SCORING_CRITERIA_VALUES"; payload: string[] }
  | {
      type: "SET_PREVIEW_STEP";
      payload: AutopilotSessionPreviewResponse | null;
    }
  | {
      type: "SET_REMIX_PREVIEW_STEP";
      payload: AutopilotSessionPreviewResponse | null;
    }
  | { type: "SET_CURATED_SEARCH_ID"; payload: number | null }
  | {
      type: "SET_REMIXED_FILTERS";
      payload: InternalCuratedSearchFilters | null;
    }
  | { type: "SET_REMIX_CURATED_SEARCH_ID"; payload: number | null }
  | { type: "SET_REMIX_PROMPT"; payload: string | undefined }
  | { type: "SET_SPECIFIC_COMPANIES"; payload: string[] }
  | { type: "SET_PREVIEW_CONTACT_FEEDBACK"; payload: PreviewContactFeedbackMap }
  | {
      type: "SET_REMIX_PREVIEW_CONTACT_FEEDBACK";
      payload: PreviewContactFeedbackMap;
    }
  | {
      type: "RESET_HYDRATION_VALUES";
      payload: IInternalAutopilotHydrationValues;
    };

const initialState: State = {
  prompt: "",
  savedFilters: null,
  expandFilters: null,
  expandPrompt: "",
  scoringCriteriaValues: [],
  expandScoringCriteriaValues: [],
  previewStep: null,
  remixPreviewStep: null,
  curatedSearchId: null,
  remixedFilters: null,
  remixCuratedSearchId: null,
  remixPrompt: undefined,
  specificCompanies: [],
  previewContactFeedback: new Map<number, IAutopilotPreviewContactFeedback>(),
  remixPreviewContactFeedback: new Map<
    number,
    IAutopilotPreviewContactFeedback
  >(),
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_PROMPT":
      return { ...state, prompt: action.payload };
    case "SET_SAVED_FILTERS":
      return { ...state, savedFilters: action.payload };
    case "SET_EXPAND_FILTERS":
      return { ...state, expandFilters: action.payload };
    case "SET_EXPAND_PROMPT":
      return { ...state, expandPrompt: action.payload };
    case "SET_SCORING_CRITERIA_VALUES":
      return { ...state, scoringCriteriaValues: action.payload };
    case "SET_EXPAND_SCORING_CRITERIA_VALUES":
      return { ...state, expandScoringCriteriaValues: action.payload };
    case "SET_PREVIEW_STEP":
      return { ...state, previewStep: action.payload };
    case "SET_REMIX_PREVIEW_STEP":
      return { ...state, remixPreviewStep: action.payload };
    case "SET_CURATED_SEARCH_ID":
      return { ...state, curatedSearchId: action.payload };
    case "SET_REMIXED_FILTERS":
      return { ...state, remixedFilters: action.payload };
    case "SET_REMIX_CURATED_SEARCH_ID":
      return { ...state, remixCuratedSearchId: action.payload };
    case "SET_REMIX_PROMPT":
      return { ...state, remixPrompt: action.payload };
    case "SET_SPECIFIC_COMPANIES":
      return { ...state, specificCompanies: action.payload };
    case "SET_PREVIEW_CONTACT_FEEDBACK":
      return { ...state, previewContactFeedback: action.payload };
    case "SET_REMIX_PREVIEW_CONTACT_FEEDBACK":
      return { ...state, remixPreviewContactFeedback: action.payload };
    case "RESET_HYDRATION_VALUES":
      return {
        ...state,
        prompt: action.payload.prompt || "",
        savedFilters: action.payload.savedFilters || null,
        expandFilters: action.payload.expandFilters || null,
        expandPrompt: action.payload.expandPrompt || "",
        scoringCriteriaValues: action.payload.scoringCriteriaValues || [],
        expandScoringCriteriaValues:
          action.payload.expandScoringCriteriaValues || [],
        previewStep: action.payload.previewStep || null,
        remixPrompt: action.payload.remixPrompt || undefined,
        remixedFilters: action.payload.remixedFilters || null,
        remixCuratedSearchId: action.payload.remixCuratedSearchId || null,
        specificCompanies: action.payload.specificCompanies || [],
        curatedSearchId: action.payload.curatedSearchId || null,
      };
    default:
      return state;
  }
}

const InternalAutopilotHydrationContextContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Memoize all setter functions to prevent recreation on every render
  const setPrompt = useCallback((prompt: string) => {
    dispatch({ type: "SET_PROMPT", payload: prompt });
  }, []);

  const setSavedFilters = useCallback(
    (filters: InternalCuratedSearchFilters | null) => {
      dispatch({ type: "SET_SAVED_FILTERS", payload: filters });
    },
    [],
  );

  const setExpandFilters = useCallback(
    (filters: InternalCuratedSearchFilters | null) => {
      dispatch({ type: "SET_EXPAND_FILTERS", payload: filters });
    },
    [],
  );

  const setExpandPrompt = useCallback((prompt: string) => {
    dispatch({ type: "SET_EXPAND_PROMPT", payload: prompt });
  }, []);

  const setScoringCriteriaValues = useCallback((values: string[]) => {
    dispatch({ type: "SET_SCORING_CRITERIA_VALUES", payload: values });
  }, []);

  const setExpandScoringCriteriaValues = useCallback((values: string[]) => {
    dispatch({ type: "SET_EXPAND_SCORING_CRITERIA_VALUES", payload: values });
  }, []);

  const setPreviewStep = useCallback(
    (step: AutopilotSessionPreviewResponse | null) => {
      dispatch({ type: "SET_PREVIEW_STEP", payload: step });
    },
    [],
  );

  const setCuratedSearchId = useCallback((id: number | null) => {
    dispatch({ type: "SET_CURATED_SEARCH_ID", payload: id });
  }, []);

  const setRemixCuratedSearchId = useCallback((id: number | null) => {
    dispatch({ type: "SET_REMIX_CURATED_SEARCH_ID", payload: id });
  }, []);

  const setRemixPrompt = useCallback((prompt: string | undefined) => {
    dispatch({ type: "SET_REMIX_PROMPT", payload: prompt });
  }, []);

  const setRemixPreviewStep = useCallback(
    (step: AutopilotSessionPreviewResponse | null) => {
      dispatch({ type: "SET_REMIX_PREVIEW_STEP", payload: step });
    },
    [],
  );

  const setRemixedFilters = useCallback(
    (filters: InternalCuratedSearchFilters | null) => {
      dispatch({ type: "SET_REMIXED_FILTERS", payload: filters });
    },
    [],
  );

  const setSpecificCompanies = useCallback((companies: string[]) => {
    dispatch({ type: "SET_SPECIFIC_COMPANIES", payload: companies });
  }, []);

  const setPreviewContactFeedback = useCallback(
    (feedback: PreviewContactFeedbackMap) => {
      dispatch({ type: "SET_PREVIEW_CONTACT_FEEDBACK", payload: feedback });
    },
    [],
  );

  const setRemixPreviewContactFeedback = useCallback(
    (feedback: PreviewContactFeedbackMap) => {
      dispatch({
        type: "SET_REMIX_PREVIEW_CONTACT_FEEDBACK",
        payload: feedback,
      });
    },
    [],
  );

  const resetHydrationValues = useCallback(
    (values: IInternalAutopilotHydrationValues) => {
      dispatch({ type: "RESET_HYDRATION_VALUES", payload: values });
    },
    [],
  );

  const previousResults = useMemo(() => {
    const hasRemixFeedback =
      state.remixPreviewContactFeedback &&
      state.remixPreviewContactFeedback.size > 0;
    const latestFeedback = hasRemixFeedback
      ? state.remixPreviewContactFeedback
      : state.previewContactFeedback;

    const latestPreviewState = hasRemixFeedback
      ? state.remixPreviewStep
      : state.previewStep;
    return latestPreviewState?.contacts.reduce<IPreviousResultsWithFeedback[]>(
      (acc, contact) => {
        if (!contact.org_contact?.id) return acc;
        const feedback = latestFeedback?.get(contact.org_contact.id);

        const feedbackPrompts = {
          wrongPositionLevel:
            "This contact is not at the position level I am looking for",
          wrongCompanyType:
            "This contact is not at the company type I am looking for",
          missingRequiredSkills:
            "This contact is missing the required skills I am looking for",
          wrongRole:
            "This contact is not in the role I am looking for, do not return contacts with this role",
        };

        const userFeedbackComment = feedback?.feedbackDetails
          ? Object.entries(feedback.feedbackDetails)
              .filter(([key, value]) => key in feedbackPrompts && value)
              .map(
                ([key]) => feedbackPrompts[key as keyof typeof feedbackPrompts],
              )
              .join(", ") +
            " " +
            feedback.feedbackDetails.additionalFeedback
          : null;

        const userFeedbackScore = feedback?.isGoodFit
          ? 10
          : feedback?.isBadFit
            ? 1
            : null;

        acc.push({
          contact: {
            firstName: contact.org_contact?.first_name,
            lastName: contact.org_contact?.last_name,
            title: contact.org_contact?.title,
            companyName: contact.org_contact?.company_name,
            rationale: contact.rationale,
            scoringCriteriaResults: contact.scoringCriteriaResults?.map(
              (criteria: IScoringCriteriaResult) => ({
                criterion_fit: criteria.criterion_fit,
                reasoning: criteria.reasoning,
                criterion_id: criteria.criterion_id,
              }),
            ),
          },
          userFeedbackScore,
          userFeedbackComment,
        });
        return acc;
      },
      [],
    );
  }, [
    state.remixPreviewContactFeedback,
    state.remixPreviewStep,
    state.previewContactFeedback,
    state.previewStep,
  ]);

  const remixContext = useMemo(
    () => ({
      remixPrompt: state.remixPrompt,
      previousInternalSearchFilters: {
        companyKeywords: state.savedFilters?.companyKeywords,
        jobTitles: state.savedFilters?.jobTitles,
        industries: state.savedFilters?.industries?.map((i) => ({
          label: i.label,
        })),
        geographies: state.savedFilters?.geographies?.map((g) => ({
          id: g.id,
          label: g.label,
        })),
        companyHQLocations: state.savedFilters?.companyHQLocations?.map(
          (c) => ({
            id: c.id,
            label: c.label,
          }),
        ),
        companyHeadCountRange: {
          lowerBound: state.savedFilters?.companyHeadCountRange?.lowerBound,
          upperBound: state.savedFilters?.companyHeadCountRange?.upperBound,
        },
        companyNames: state.savedFilters?.companyNames?.map((c) => ({
          label: c.label,
        })),
      },
      previousResults,
    }),
    [state.savedFilters, state.remixPrompt, previousResults],
  );

  const expandContext = useMemo(() => {
    if (!state.expandPrompt) return undefined;

    return {
      remixPrompt: state.expandPrompt,
      previousInternalSearchFilters: {
        companyKeywords: state.expandFilters?.companyKeywords,
        jobTitles: state.expandFilters?.jobTitles,
        industries: state.expandFilters?.industries?.map((i) => ({
          label: i.label,
        })),
        geographies: state.expandFilters?.geographies?.map((g) => ({
          id: g.id,
          label: g.label,
        })),
        companyHQLocations: state.expandFilters?.companyHQLocations?.map(
          (c) => ({
            id: c.id,
            label: c.label,
          }),
        ),
        companyHeadCountRange: {
          lowerBound: state.expandFilters?.companyHeadCountRange?.lowerBound,
          upperBound: state.expandFilters?.companyHeadCountRange?.upperBound,
        },
        companyNames: state.expandFilters?.companyNames?.map((c) => ({
          label: c.label,
        })),
      },
    };
  }, [state.expandFilters, state.expandPrompt]);

  // Memoize the entire context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      prompt: state.prompt,
      setPrompt,
      savedFilters: state.savedFilters,
      setSavedFilters,
      expandFilters: state.expandFilters,
      setExpandFilters,
      expandPrompt: state.expandPrompt,
      setExpandPrompt,
      scoringCriteriaValues: state.scoringCriteriaValues,
      setScoringCriteriaValues,
      previewStep: state.previewStep,
      setPreviewStep,
      curatedSearchId: state.curatedSearchId,
      setCuratedSearchId,
      remixCuratedSearchId: state.remixCuratedSearchId,
      setRemixCuratedSearchId,
      remixPrompt: state.remixPrompt,
      setRemixPrompt,
      remixPreviewStep: state.remixPreviewStep,
      setRemixPreviewStep,
      remixedFilters: state.remixedFilters,
      setRemixedFilters,
      remixContext,
      expandContext,
      expandScoringCriteriaValues: state.expandScoringCriteriaValues,
      setExpandScoringCriteriaValues,
      specificCompanies: state.specificCompanies,
      setSpecificCompanies,
      previewContactFeedback: state.previewContactFeedback,
      setPreviewContactFeedback,
      remixPreviewContactFeedback: state.remixPreviewContactFeedback,
      setRemixPreviewContactFeedback,
      resetHydrationValues,
    }),
    [
      state.prompt,
      setPrompt,
      state.savedFilters,
      setSavedFilters,
      state.expandFilters,
      setExpandFilters,
      state.expandPrompt,
      setExpandPrompt,
      state.expandScoringCriteriaValues,
      setExpandScoringCriteriaValues,
      state.scoringCriteriaValues,
      setScoringCriteriaValues,
      state.previewStep,
      setPreviewStep,
      state.curatedSearchId,
      setCuratedSearchId,
      state.remixCuratedSearchId,
      setRemixCuratedSearchId,
      state.remixPrompt,
      setRemixPrompt,
      state.remixPreviewStep,
      setRemixPreviewStep,
      state.remixedFilters,
      setRemixedFilters,
      remixContext,
      expandContext,
      state.specificCompanies,
      setSpecificCompanies,
      state.previewContactFeedback,
      setPreviewContactFeedback,
      state.remixPreviewContactFeedback,
      setRemixPreviewContactFeedback,
      resetHydrationValues,
    ],
  );

  return (
    <InternalAutopilotHydrationContext.Provider value={contextValue}>
      {children}
    </InternalAutopilotHydrationContext.Provider>
  );
};

export default InternalAutopilotHydrationContextContainer;
